/**
 * One ratio against a limit - availability against 100%, a latency against its
 * threshold. A meter, not a one-bar bar chart.
 *
 * The track is a lighter step of the fill's own ramp rather than a neutral grey, so
 * state reads across the whole bar instead of only across the filled part. The fill
 * carries severity, and the value is always printed beside it: the bar is the glance,
 * the number is the answer, and neither depends on the other.
 */

import { cn } from '../../lib/cn'

interface MeterProps {
  /** 0-1. Clamped, so a latency past its ceiling fills the bar rather than overflowing. */
  ratio: number
  fill: string
  label: string
  /** Rendered beside the bar. Never only in a tooltip. */
  value: string
  /** Where the limit sits, as a 0-1 position, drawn as a hairline. */
  threshold?: number
  thresholdLabel?: string
  className?: string
}

export function Meter({
  ratio,
  fill,
  label,
  value,
  threshold,
  thresholdLabel,
  className,
}: MeterProps) {
  const clamped = Math.min(Math.max(Number.isFinite(ratio) ? ratio : 0, 0), 1)

  return (
    <div className={cn('min-w-0', className)}>
      <div className="mb-1 flex items-baseline justify-between gap-2">
        <span className="truncate text-xs text-ink-secondary">{label}</span>
        <span className="tabular shrink-0 text-xs font-semibold text-ink">{value}</span>
      </div>

      <div
        className="relative h-2 w-full overflow-hidden rounded-full"
        style={{ backgroundColor: 'var(--color-track)' }}
        role="meter"
        aria-valuenow={Math.round(clamped * 100)}
        aria-valuemin={0}
        aria-valuemax={100}
        aria-label={`${label}: ${value}`}
      >
        {/* Square at the baseline, 4px rounded at the data end. */}
        <div
          className="h-full rounded-r"
          style={{
            width: `${clamped * 100}%`,
            backgroundColor: fill,
            transition: 'width 200ms ease-out',
          }}
        />

        {threshold !== undefined && threshold > 0 && threshold < 1 ? (
          <span
            aria-hidden
            title={thresholdLabel}
            className="absolute top-0 h-full w-px"
            style={{ left: `${threshold * 100}%`, backgroundColor: 'var(--color-axis)' }}
          />
        ) : null}
      </div>
    </div>
  )
}
